import { privacyDocument, termsDocument } from "../components/marketing/legalDocuments.ts";

export const defaultShareImage = "/og/maito.png";

/** The share card the build renders for a page: `/blog/post` → `/og/blog/post.png`. */
export const shareImagePath = (path: string) => `/og${path}.png`;

/** A page's share image: its own `image`, or its rendered share card. */
export const shareImage = (page: { readonly path: string; readonly image?: string }) =>
  page.image ?? shareImagePath(page.path);

export const publicPages = {
  "/about": {
    title: "About Maito: Why We Built an AI Support Engineer",
    description:
      "Why we built Maito: support shouldn’t wait for the one engineer who knows. Maito answers escalations from the ticket, code, and logs, then helps the rest of the company with what it learns.",
  },
  "/product/company-knowledge": {
    title: "AI Knowledge Base: Explain It Once, Your Team Can Use It",
    description:
      "An AI knowledge base without the wiki. Maito remembers what your team explains in shared conversations, from how a feature works to why a bug was fixed, and uses it in the next escalation. Ask in Slack or any AI assistant. See what it knows, correct it, and restore earlier versions.",
  },
  "/product/team-collaboration": {
    title: "Team Collaboration: Support, Engineering, and Customers in One Thread",
    description:
      "Your team works in Slack, Claude, ChatGPT, Cursor, Codex, Grok, and Muse. Maito is one AI agent they all share. On an escalation, it asks the customer for details, asks the engineer who owns the code, and keeps everyone in one thread. Connect it in minutes.",
  },
  "/product/skills": {
    title: "AI Agent Skills: Save Your Team’s Way of Doing Things",
    description:
      "Save how your team handles a job, like triaging an export bug, as an AI agent skill. Anyone can run it from Slack or add it to a Loop. Every change is reviewed, and skills never give Maito more access.",
  },
  "/use-cases/engineering": {
    title: "AI Coding Agent for Teams: Escalations to Reviewed Pull Requests",
    description:
      "Stop losing afternoons to support escalations. Maito checks the ticket, code, and logs, fixes the bug on its own branch, runs your checks, and opens a pull request for an engineer to review. Hand it your own bugs and features from Slack, Cursor, or Claude too.",
  },
  "/use-cases/qa": {
    title: "AI Software Testing: Browser Tests With Video and Bug Reports",
    description:
      "AI software testing for QA teams. Ask Maito to reproduce a customer’s bug or test a branch from Slack or Claude. It clicks through your app in a real browser, records a video, checks the baseline, and writes the bug report.",
  },
  "/use-cases/support": {
    title: "AI Support Engineer for Escalations, Bug Fixes, and Replies",
    description:
      "An AI support engineer for B2B software teams. Support asks @Maito in Slack. It checks the ticket, code, and logs, answers, opens a fix for engineers to review, and drafts a reply that waits for approval. Connect Plain, Pylon, Intercom, or Help Scout, or forward from any help desk, and see how many escalations it saves.",
  },
  "/use-cases/product": {
    title: "AI Agent for Product Management: Answers, Specs, and Prototypes",
    description:
      "An AI agent for product managers. See what customers keep asking support for, and ask how your product really works in Slack or in Claude. Maito answers from the code, past decisions, and escalations, writes specs with the edge cases, and builds prototypes.",
  },
  "/use-cases/agencies": {
    title: "AI for Agencies: Handle Client Bug Reports After Launch",
    description:
      "Clients report bugs by email or Slack. Maito checks their repository and logs, opens a fix for your team to review, and drafts the reply to the client. It handles small changes and monthly checks too. One workspace per client keeps their work apart.",
  },
  "/workflows": {
    title: "AI Workflows for Support and Engineering Teams",
    description:
      "AI workflow examples for software companies: support escalations, bug reports, QA, product feedback, and engineering. See the steps, a real example, and how to set each one up in Slack.",
  },
  "/integrations": {
    title: "Integrations and MCP Servers: Connect Maito to 600+ Tools",
    description:
      "Connect Salesforce, Notion, Linear, Sentry, Stripe and 600+ more tools to Maito through their MCP servers in one click, or add your own. Connect once, and your whole team can use it from Slack, Claude, ChatGPT, Cursor, Codex, Grok or Muse.",
  },
  "/services/maito-setup": {
    title: "Maito Setup Service: Your Help Desk, Code, and First Escalations",
    description:
      "For $2,999 once, we connect your help desk, repository, logs, and Slack, run your first 10 escalations with you, and set up the weekly escalation report. Then $149 a month per workspace.",
  },
  "/homepage": {
    title: "AI Agent for Support Escalations",
    description:
      "Your support team asks Maito in Slack or in their AI assistant. It checks the ticket, code, and logs, answers, and opens a fix for engineers to review. Setup takes minutes.",
  },
  "/pricing": {
    title: "Pricing: $149 per Workspace per Month",
    description:
      "$149 per workspace per month with unlimited people and $20 in cloud credits. Bring your own AI provider, or have us set everything up for $2,999. Self-setup takes minutes.",
  },
  "/faq": {
    title: "FAQ: Help Desks, Escalations, Setup, Providers & Pricing",
    description:
      "Answers about Maito, the AI support engineer: working with your help desk, what happens when support asks about a bug, reviewing fixes, setup, AI provider costs, and workspace pricing.",
  },
  "/terms": { title: termsDocument.title, description: termsDocument.description },
  "/privacy": { title: privacyDocument.title, description: privacyDocument.description },
} as const;

export type PublicPagePath = keyof typeof publicPages;

export function isPublicPagePath(path: string): path is PublicPagePath {
  return Object.hasOwn(publicPages, path);
}

export function publicPage(path: PublicPagePath) {
  return { ...publicPages[path], path, kind: "marketing" as const };
}

/** Public identity of the homepage, which also has an explicit marketing route. */
export function canonicalPagePath(path: string) {
  return path === "/homepage" ? "/" : path;
}

export function pageTitle(title: string, path: string) {
  if (title.endsWith("Maito")) return title;
  return path === "/homepage" ? `Maito: ${title}` : `${title} | Maito`;
}
